// Late Accounts Analyzer
// Handles analysis of late accounts data including cumulative timeline trends

import { BaseAnalyzer } from './BaseAnalyzer.js';

export class LateAccountsAnalyzer extends BaseAnalyzer {
  constructor() {
    super('lateAccounts');
  }

  /**
   * Analyze late accounts data
   * @param {Array} records - Late accounts records
   * @param {Array} summaryRecords - Summary records from partner table
   * @param {Date} startDate - Filter start date (optional)
   * @param {Date} endDate - Filter end date (optional)
   * @returns {Object} - Analysis results
   */
  analyze(records, summaryRecords = [], signedRecords = [], startDate = null, endDate = null) {
    this.logAnalysisStart('late accounts', records.length);

    if (records.length === 0) {
      return {
        byPartner: {},
        byStatus: {},
        byYearEnd: {},
        totalClients: 0,
        partnersInvolved: 0,
        summaryStats: {},
        cumulativeTimeline: []
      };
    }

    // Basic partner and status distribution
    const partnerCounts = this.groupBy(records, 'partner');
    const statusCounts = this.groupBy(records, 'status');
    
    // Year-end analysis
    const yearEndCounts = {};
    records.forEach((record) => {
      if (record.finYearEnd instanceof Date) {
        const year = record.finYearEnd.getFullYear();
        yearEndCounts[year] = (yearEndCounts[year] || 0) + 1;
      }
    });

    // Extract summary stats from summary table
  const summaryStats = this.extractSummaryStats(summaryRecords, signedRecords, records);

    // Create cumulative timeline with date filtering
  const cumulativeTimeline = this.createCumulativeTimeline(records, summaryRecords, signedRecords, startDate, endDate);

    // Get current partner counts from timeline (latest month) for accurate overview card
    const currentPartnerCounts = this.getCurrentPartnerCounts(cumulativeTimeline, partnerCounts);

    this.logAnalysisComplete('late accounts');

    return {
      byPartner: currentPartnerCounts,
      byStatus: statusCounts,
      byYearEnd: yearEndCounts,
      totalClients: records.length,
      partnersInvolved: Object.keys(currentPartnerCounts).length,
      summaryStats,
      cumulativeTimeline
    };
  }

  /**
   * Extract summary statistics from summary table
   * @param {Array} summaryRecords - Summary records
   * @returns {Object} - Summary statistics
   */
  extractSummaryStats(summaryRecords, signedRecords = [], detailRecords = []) {
    const stats = {
      totalLateAccounts: 0,
      totalSignedAccounts: 0,
      partnerBreakdown: {}
    };

    const signedCounts = {};
    signedRecords.forEach(record => {
      const partner = record.partnerNow || record.partner || record.data?.Partner || 'Unassigned';
      signedCounts[partner] = (signedCounts[partner] || 0) + 1;
    });

    const lateCounts = {};
    detailRecords.forEach(record => {
      const partner = record.partnerNow || record.partner || record.data?.Partner || 'Unassigned';
      lateCounts[partner] = (lateCounts[partner] || 0) + 1;
    });

    summaryRecords.forEach(record => {
      const partner = record.partnerNow || record.partner;
      const lateCount = record.lateAccountsCount || 0;
      const signedFromDataset = signedCounts.hasOwnProperty(partner)
        ? signedCounts[partner]
        : null;
      const signedCount = signedFromDataset !== null
        ? signedFromDataset
        : (record.signedAccountsCount || 0);

      stats.totalLateAccounts += lateCount;
      stats.totalSignedAccounts += signedCount;
      
      stats.partnerBreakdown[partner] = {
        lateAccounts: lateCount,
        signedAccounts: signedCount
      };

      if (signedFromDataset !== null) {
        delete signedCounts[partner];
      }
    });

    if (stats.totalLateAccounts === 0) {
      Object.entries(lateCounts).forEach(([partner, count]) => {
        stats.partnerBreakdown[partner] = stats.partnerBreakdown[partner] || { lateAccounts: 0, signedAccounts: 0 };
        stats.partnerBreakdown[partner].lateAccounts = count;
        stats.totalLateAccounts += count;
      });
    }

    Object.entries(signedCounts).forEach(([partner, count]) => {
      stats.partnerBreakdown[partner] = stats.partnerBreakdown[partner] || { lateAccounts: lateCounts[partner] || 0, signedAccounts: 0 };
      stats.partnerBreakdown[partner].signedAccounts = count;
      stats.totalSignedAccounts += count;
    });

    return stats;
  }

  /**
   * Create cumulative timeline showing late accounts over time
   * @param {Array} records - Detail records
   * @param {Array} summaryRecords - Summary records
   * @param {Date} startDate - Filter start date (optional)
   * @param {Date} endDate - Filter end date (optional)
   * @returns {Array} - Timeline data
   */
  createCumulativeTimeline(records, summaryRecords, signedRecords = [], startDate = null, endDate = null) {
    // Find earliest financial year-end date for fallback
    const earliestDate = this.findEarliestFinYearEnd(records);
    if (!earliestDate) {
      console.log('    ⚠️ No valid financial year-end dates found');
      return [];
    }

    // Determine timeline date range
    const minTimelineStart = new Date(2025, 0, 1);
    let timelineStartDate = startDate ? new Date(startDate) : new Date(earliestDate);
    let timelineEndDate = endDate ? new Date(endDate) : new Date();

    if (timelineStartDate < minTimelineStart) {
      timelineStartDate = new Date(minTimelineStart);
    }

    if (timelineEndDate < timelineStartDate) {
      timelineEndDate = new Date(timelineStartDate);
    }

    console.log(`    📅 Timeline window: ${timelineStartDate.toDateString()} to ${timelineEndDate.toDateString()} (min start anchored to Jan 2025)`);
    
    // Generate monthly timeline from start to end date
  const timeline = this.generateMonthlyTimeline(timelineStartDate, timelineEndDate);

    // Calculate baseline counts at the start of the period
  const baselineCounts = this.calculateBaselineAtDate(records, summaryRecords, signedRecords, timelineStartDate);

    // Create signed accounts timeline - only include signings within the date range
  const signedTimeline = this.createSignedAccountsTimeline(records, signedRecords, timelineStartDate, timelineEndDate);

    // Calculate cumulative counts for each month
    const partners = Object.keys(baselineCounts);
    const timelineData = [];

    timeline.forEach(monthData => {
      const monthKey = monthData.key;
      const monthDate = monthData.date;
      
      const monthRecord = {
        month: monthKey,
        date: monthDate,
        partners: {},
        total: 0,
        signedByPartner: {},
        cumulativeSignedByPartner: {},
        signedThisMonth: 0,
        cumulativeSignedTotal: 0,
      };

      let monthTotal = 0;
      let monthSignedTotal = 0;
      let cumulativeSignedTotal = 0;

      // Calculate for each partner
      partners.forEach(partner => {
        const baselineCount = baselineCounts[partner] || 0;
        
        // Count how many accounts were signed from timeline start up to this month
        const cumulativeSigned = this.countSignedAccountsUpToMonth(
          signedTimeline[partner] || [], 
          monthDate
        );

        // Count signings that happened within the month
        const monthlySigned = this.countSignedAccountsInMonth(
          signedTimeline[partner] || [],
          monthDate
        );
        
        // Current late accounts = baseline count - accounts signed since timeline start
        const currentLateAccounts = Math.max(0, baselineCount - cumulativeSigned);
        
        monthRecord.partners[partner] = currentLateAccounts;
        monthRecord.signedByPartner[partner] = monthlySigned;
        monthRecord.cumulativeSignedByPartner[partner] = cumulativeSigned;
        
        monthTotal += currentLateAccounts;
        monthSignedTotal += monthlySigned;
        cumulativeSignedTotal += cumulativeSigned;
      });

      monthRecord.total = monthTotal;
      monthRecord.signedThisMonth = monthSignedTotal;
      monthRecord.cumulativeSignedTotal = cumulativeSignedTotal;
      timelineData.push(monthRecord);
    });

    const timelineType = startDate ? "user-selected start to today" : "full historical data to today";
    console.log(`    📈 Generated ${timelineData.length} months of timeline data with baseline approach (${timelineType})`);
    return timelineData;
  }

  /**
   * Find earliest financial year-end date
   * @param {Array} records - Detail records
   * @returns {Date|null} - Earliest date or null
   */
  findEarliestFinYearEnd(records) {
    let earliestDate = null;
    
    records.forEach(record => {
      let dateValue = null;
      
      // Handle both Date objects and date strings
      if (record.finYearEnd instanceof Date) {
        dateValue = record.finYearEnd;
      } else if (typeof record.finYearEnd === 'string' && record.finYearEnd.length > 0) {
        // Try to parse date strings (including ISO format)
        try {
          dateValue = new Date(record.finYearEnd);
          // Validate the parsed date
          if (isNaN(dateValue.getTime())) {
            dateValue = null;
          }
        } catch (e) {
          dateValue = null;
        }
      }
      
      if (dateValue && !isNaN(dateValue.getTime())) {
        if (!earliestDate || dateValue < earliestDate) {
          earliestDate = dateValue;
        }
      }
    });

    if (earliestDate) {
      console.log(`    ✅ Found earliest financial year-end: ${earliestDate.toDateString()}`);
    } else {
      console.log('    ⚠️ No valid financial year-end dates found');
    }

    return earliestDate;
  }

  /**
   * Get initial counts for each partner from summary table
   * @param {Array} summaryRecords - Summary records
   * @returns {Object} - Partner initial counts
   */
  getPartnerInitialCounts(summaryRecords, records = [], signedRecords = []) {
    const summaryTotals = {};
    summaryRecords.forEach(record => {
      const partner = record.partnerNow || record.partner || 'Unassigned';
      const lateCount = record.lateAccountsCount || 0;
      const signedCount = record.signedAccountsCount || 0;
      summaryTotals[partner] = lateCount + signedCount;
    });

    const lateCounts = {};
    records.forEach(record => {
      const partner = record.partnerNow || record.partner || record.data?.Partner || 'Unassigned';
      lateCounts[partner] = (lateCounts[partner] || 0) + 1;
    });

    const signedCounts = {};
    signedRecords.forEach(record => {
      const partner = record.partnerNow || record.partner || record.data?.Partner || 'Unassigned';
      signedCounts[partner] = (signedCounts[partner] || 0) + 1;
    });

    const counts = {};
    const partners = new Set([
      ...Object.keys(summaryTotals),
      ...Object.keys(lateCounts),
      ...Object.keys(signedCounts),
    ]);

    partners.forEach(partner => {
      const fromSummary = summaryTotals[partner] || 0;
      const computedTotal = (lateCounts[partner] || 0) + (signedCounts[partner] || 0);
      counts[partner] = Math.max(fromSummary, computedTotal);
    });

    return counts;
  }

  /**
   * Create timeline of when accounts were signed
   * @param {Array} records - Detail records
   * @param {Array} signedRecords - Additional signed records
   * @param {Date} startDate - Filter start date (optional)
   * @param {Date} endDate - Filter end date (optional)
   * @returns {Object} - Partner signed accounts timeline
   */
  createSignedAccountsTimeline(records, signedRecords = [], startDate = null, endDate = null) {
    const timeline = {};

    const addToTimeline = (partner, auditDate, clientName) => {
      if (!auditDate) return;
      if (startDate && endDate) {
        if (auditDate < startDate || auditDate > endDate) {
          return;
        }
      }

      if (!timeline[partner]) {
        timeline[partner] = [];
      }

      timeline[partner].push({
        clientName,
        auditReportDate: auditDate,
      });
    };
    
    records.forEach(record => {
      const partner = record.partnerNow || record.partner;
      
      // Only include accounts that have been signed AND have audit report date
      if (record.status && record.status.toLowerCase() === 'signed' && record.auditReportDate) {
        
        // Parse audit report date to handle both Date objects and strings
        const auditDate = this.parseDateValue(record.auditReportDate);
        const partnerName = partner || record.data?.Partner || 'Unassigned';

        addToTimeline(partnerName, auditDate, record.clientName);
      }
    });

    signedRecords.forEach(record => {
      const partner = record.partnerNow || record.partner || record.data?.Partner || 'Unassigned';
      const auditDate = this.parseDateValue(
        record.auditReportDate ||
        record.data?.['Engagement Sign Off Date'] ||
        record.data?.['Date of Auditor\'s Report']
      );

      addToTimeline(partner, auditDate, record.clientName || record.data?.['Engagement Name']);
    });

    return timeline;
  }

  /**
   * Count signed accounts up to a specific month
   * @param {Array} signedAccounts - Signed accounts for a partner
   * @param {Date} monthDate - Month date to count up to
   * @returns {number} - Count of signed accounts
   */
  countSignedAccountsUpToMonth(signedAccounts, monthDate) {
    let count = 0;
    
    signedAccounts.forEach(account => {
      if (account.auditReportDate <= monthDate) {
        count++;
      }
    });

    return count;
  }

  countSignedAccountsInMonth(signedAccounts = [], monthDate) {
    let count = 0;

    signedAccounts.forEach(account => {
      if (!account || !account.auditReportDate) {
        return;
      }

      const auditDate = account.auditReportDate;
      if (
        auditDate.getFullYear() === monthDate.getFullYear() &&
        auditDate.getMonth() === monthDate.getMonth()
      ) {
        count++;
      }
    });

    return count;
  }

  /**
   * Calculate baseline late account counts at a specific date
   * @param {Array} records - All detail records
   * @param {Array} summaryRecords - Summary records
   * @param {Date} baselineDate - Date to calculate baseline at
   * @returns {Object} - Partner baseline counts
   */
  calculateBaselineAtDate(records, summaryRecords, signedRecords = [], baselineDate) {
    // Get total accounts ever assigned to each partner
    const partnerTotalCounts = this.getPartnerInitialCounts(summaryRecords, records, signedRecords);
    
    // Count how many accounts each partner had signed BEFORE the baseline date
    const signedBeforeBaseline = {};
    
    const addSignedBeforeBaseline = (partner, date) => {
      if (!partner || !date) return;
      if (date < baselineDate) {
        signedBeforeBaseline[partner] = (signedBeforeBaseline[partner] || 0) + 1;
      }
    };

    records.forEach(record => {
      const partner = record.partnerNow || record.partner || record.data?.Partner || 'Unassigned';
      const auditDate = this.parseDateValue(record.auditReportDate);
      if (record.status && record.status.toLowerCase() === 'signed') {
        addSignedBeforeBaseline(partner, auditDate);
      }
    });

    signedRecords.forEach(record => {
      const partner = record.partnerNow || record.partner || record.data?.Partner || 'Unassigned';
      const auditDate = this.parseDateValue(
        record.auditReportDate ||
        record.data?.['Engagement Sign Off Date'] ||
        record.data?.['Date of Auditor\'s Report']
      );
      addSignedBeforeBaseline(partner, auditDate);
    });
    
    // Calculate baseline: total accounts - accounts signed before baseline date
    const baselineCounts = {};
    Object.keys(partnerTotalCounts).forEach(partner => {
      const totalCount = partnerTotalCounts[partner] || 0;
      const signedCount = signedBeforeBaseline[partner] || 0;
      baselineCounts[partner] = Math.max(0, totalCount - signedCount);
    });
    
    console.log(`    📊 Baseline counts at ${baselineDate.toDateString()}:`, baselineCounts);
    return baselineCounts;
  }

  /**
   * Get current partner counts from timeline data (latest month)
   * @param {Array} timelineData - Timeline data from createCumulativeTimeline
   * @param {Object} fallbackCounts - Fallback partner counts if no timeline data
   * @returns {Object} - Current partner counts
   */
  getCurrentPartnerCounts(timelineData, fallbackCounts) {
    if (!timelineData || timelineData.length === 0) {
      // No timeline data - use fallback
      console.log(`    📊 Using fallback partner counts (no timeline data)`);
      return fallbackCounts;
    }

    // Get the latest month's data
    const latestMonth = timelineData[timelineData.length - 1];
    
    if (!latestMonth || !latestMonth.partners) {
      // No partner data in latest month - use fallback
      console.log(`    📊 Using fallback partner counts (no partner data in timeline)`);
      return fallbackCounts;
    }

    // Return the current partner counts from timeline
    console.log(`    📊 Using timeline partner counts from ${latestMonth.month}:`, latestMonth.partners);
    return latestMonth.partners;
  }

  /**
   * Generate monthly timeline between two dates
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Array} - Monthly timeline
   */
  generateMonthlyTimeline(startDate, endDate) {
    const timeline = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    
    while (current <= end) {
      const monthKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
      
      // Use the last day of the month for proper timeline calculations
      // End of that day, not its midnight: a signing dated the last day of the
      // month (a date-only value, parsed as UTC midnight = 08:00 local) belongs
      // to this month's count.
      const lastDayOfMonth = new Date(current.getFullYear(), current.getMonth() + 1, 0, 23, 59, 59, 999);
      
      timeline.push({
        key: monthKey,
        date: lastDayOfMonth
      });
      
      current.setMonth(current.getMonth() + 1);
    }
    
    return timeline;
  }

  /**
   * Generate alerts for late accounts
   * @param {Array} records - Late accounts records
   * @returns {Array} - Generated alerts
   */
  generateAlerts(records) {
    const alerts = [];

    if (records.length > 20) {
      alerts.push(
        this.createAlert(
          'danger',
          'Late Accounts',
          `${records.length} late accounts require immediate attention`,
          records.length,
          'high'
        )
      );
    } else if (records.length > 10) {
      alerts.push(
        this.createAlert(
          'warning',
          'Late Accounts',
          `${records.length} late accounts need review`,
          records.length,
          'medium'
        )
      );
    }

    return alerts;
  }

  parseDateValue(value) {
    if (!value) return null;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'string') {
      try {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const parsed = new Date(trimmed);
        return isNaN(parsed.getTime()) ? null : parsed;
      } catch (error) {
        return null;
      }
    }
    return null;
  }
} 